/**
 * External dependencies
 */
import { useEffect, useCallback, useMemo } from 'react';
import useDidMount from '@naruto/naruto-hooks/src/useDidMount';
import useAutoLogout, { DEFAULT_PARAMS } from '@naruto/naruto-hooks/src/useAutoLogout';

/**
 * Internal dependencies
 */
import useEnv from '@naruto/bgdapp/src/hooks/useEnv';
import useMessage from '@naruto/bgdapp/src/hooks/useMessage';
import useReduxApi from '@naruto/bgdapp/src/hooks/useReduxApi';
import useMappedState from '@naruto/bgdapp/src/hooks/useMappedState';
import useLang from '@naruto/bgdapp/src/hooks/useLang';
import { MessageDialogList, MessageConfirmList, MessageSnackList, Option } from './types';

interface UseData {
  /* Data : General */
  userAuthCheck: boolean;
  messageDialogList: MessageDialogList;
  messageConfirmList: MessageConfirmList;
  messageSnackList: MessageSnackList;
  langOptions: [Option<string>, Option<string>];
  sizeOptions: Option<number>[];
  currentLang: string;
  fontScale: number;
  /* Functions : General */
  removeDialog: () => void;
  removeConfirm: () => void;
  removeSnack: () => void;
  handleScaleChange: (scale: number) => void;
  handleLangChange: (lang: string) => void;
}

function useData(): UseData {
  /* Global & Local State */
  const { i18ns } = useLang();
  const env = useEnv();
  const storeUser = useMappedState(state => state.user);
  const storeMessage = useMappedState(state => state.message);
  const storeGlobal = useMappedState(state => state.global);
  const reduxUser = useReduxApi('user');
  const reduxGlobal = useReduxApi('global');
  const reduxMessage = useMessage();

  /* Data */
  const langOptions: [Option<string>, Option<string>] = useMemo(
    () =>
      i18ns._getCurrentI18n().langAvailableList.map(avalible => ({
        label: i18ns.__(`frontend.global.property.language_${avalible.name}`),
        value: avalible.value
      })) as [Option<string>, Option<string>],
    [i18ns]
  );
  const sizeOptions: Option<number>[] = useMemo(
    () => [
      { label: '100%', value: 1 },
      { label: '125%', value: 1.25 },
      { label: '150%', value: 1.5 }
    ],
    []
  );
  const currentLang = useMemo(() => storeGlobal.globalLang, [storeGlobal.globalLang]);

  /* Functions */
  const handleScaleChange = useCallback(
    (scale: number): void => {
      reduxGlobal('postGlobalFontScale', [scale]);
    },
    [storeGlobal.globalFontScale]
  );
  const handleLangChange = useCallback(
    (lang: string): void => {
      i18ns.setCurrentLanguage(lang);
      reduxGlobal('postGlobalLang', [lang]);
    },
    [storeGlobal.globalLang]
  );
  const onLogout = useCallback((checkAndExpired?: boolean): void => {
    reduxUser('postUserLogout', [checkAndExpired]);
  }, []);
  /* Functions : Initialize */
  const initializeList = useCallback(() => {
    reduxUser('getUserIsLogin', []);
    reduxGlobal('postGlobalLang', [i18ns.currentI18nLanguage]);
  }, []);
  const initialize = useCallback((): void => {
    initializeList();
  }, []);

  /* Hooks */
  useDidMount(() => {
    initialize();
  });
  useEffect(() => {
    if (storeUser.userSignOutOAuthUrl.link) {
      window.location.assign(storeUser.userSignOutOAuthUrl.link);
    }
  }, [storeUser.userSignOutOAuthUrl]);
  useAutoLogout(env.config.EnableAutoLogout, storeUser.userIsLogin, {
    ...DEFAULT_PARAMS,
    logoutFunction: onLogout,
    timeoutInSeconds: env.config.AutoLogoutTime,
    autoSignOutName: env.config.AutoLogoutLocalName
  });

  /* Main */
  return {
    /* Data : General */
    userAuthCheck: storeUser.userAuthCheck,
    messageDialogList: storeMessage.messageDialogList,
    messageConfirmList: storeMessage.messageConfirmList,
    messageSnackList: storeMessage.messageSnackList,
    langOptions,
    sizeOptions,
    currentLang,
    fontScale: storeGlobal.globalFontScale,
    /* Functions : General */
    removeDialog: reduxMessage.removeDialog,
    removeConfirm: reduxMessage.removeConfirm,
    removeSnack: reduxMessage.removeSnack,
    handleScaleChange,
    handleLangChange
  };
}

export default useData;
